import { useState, useEffect } from 'react';
import { adminService } from '@/services/adminService';
import { AdminUser } from '@/types/admin';
import { UserStatusBadge } from './UserStatusBadge';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface UserManagementTableProps {
  onViewUser: (user: AdminUser) => void;
}

export function UserManagementTable({ onViewUser }: UserManagementTableProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [techFilter, setTechFilter] = useState<string>('all');

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'nonprofit_leader', label: 'Nonprofit Leader' },
    { value: 'program_manager', label: 'Program Manager' },
    { value: 'communications_staff', label: 'Communications Staff' },
    { value: 'fundraiser', label: 'Fundraiser' },
    { value: 'volunteer_coordinator', label: 'Volunteer Coordinator' },
    { value: 'other', label: 'Other' }
  ];

  const techOptions = [
    { value: 'all', label: 'All Tech Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  useEffect(() => {
    loadUsers();
  }, [search, statusFilter, roleFilter, techFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await adminService.getAllUsers({
        search,
        status: statusFilter as any,
        role: roleFilter !== 'all' ? roleFilter : undefined,
        techComfort: techFilter !== 'all' ? techFilter : undefined
      });
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
        <p className="text-gray-600">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by email or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md bg-white text-sm"
            >
              <option value="all">All Users</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive (30+ days)</option>
              <option value="at_risk">At-Risk (14+ days)</option>
              <option value="new">New</option>
              <option value="admin">Admins</option>
            </select>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md bg-white text-sm"
            >
              {roleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={techFilter}
              onChange={(e) => setTechFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md bg-white text-sm"
            >
              {techOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr
                key={user.userId}
                onClick={() => onViewUser(user)}
                className="hover:bg-blue-50 transition-colors cursor-pointer"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{user.email}</div>
                    <div className="text-sm text-gray-500">
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'No name'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <UserStatusBadge status={user.activityStatus} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{user.lessonsCompleted} lessons</div>
                  <div className="text-sm text-gray-500">{user.totalEvents} events</div>
                  <div className="text-sm text-gray-500">{user.workshopsCompleted} workshops</div>
                  <div className="text-xs text-gray-400">
                    {user.totalTimeSpent ? `${(user.totalTimeSpent / 3600).toFixed(1)}h engaged` : 'No time logged'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="text-center py-12 text-gray-500">No users found matching your criteria.</div>
      )}
    </div>
  );
}
